import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled from 'astroturf/react';

import { Button } from 'js/design_system/Button';
import { Modal } from 'js/design_system/Modal';
import $image_url from 'js/utils/image_url';
import { KiiSource } from 'js/kii_component/api/KiiDataApi';
import { fsEvent } from 'js/fullstory';
import { KiiComponentHandle, ViewType } from 'js/kii_component/KiiComponent';
import { PaperClaiming } from 'js/kii_component/PaperClaiming';
import { PaperTitle } from 'js/kii_component/PaperTitle';

const StyledModal = styled(Modal)`
  // prevents strange stretching/scrolling behavior of the modal
  // when scrolling the typeahead popover when it's at the bottom
  overflow: hidden;

  width: 859px;
  box-shadow:
    0 24px 38px 0 rgba(34, 34, 51, 0.14),
    0 9px 46px 0 rgba(34, 34, 51, 0.12),
    0 11px 15px 0 rgba(34, 34, 51, 0.2);
`;

const ModalContents = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--spacing-md-40, 40px);
`;

const TopContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const InnerContainer = styled.div`
  display: flex;
  align-self: center;
`;

const TopImage = styled.img`
  @media (max-width: 750px) {
    display: none;
  }
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-8, 8px);
`;

const HeadingText = styled.div`
  color: var(--text-primary, #223);
  font-size: var(--type-sans-serif-xl-size, 42px);
  line-height: var(--type-sans-serif-xl-line-height, 46px);
`;

const SubheadingText = styled.div`
  color: var(--text-secondary, #535366);
  font-size: var(--type-sans-serif-sm-size, 24px);
  line-height: var(--type-sans-serif-sm-line-height, 30px);
`;

const PaperTitleContainer = styled.div`
  width: 500px;
`;

type Props = {
  imageName: string;
  headingText: string;
  subheadingText: string;
  kiiSource: KiiSource;
  open: boolean;
  hide: (submitted?: boolean) => void;
  defaultViewType: ViewType;
};

export const CommonModal = ({
  imageName,
  headingText,
  subheadingText,
  kiiSource,
  defaultViewType,
  hide,
  open,
}: Props) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [viewType, setViewType] = useState(defaultViewType);

  useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);

    // Cleanup the event listener on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onSubmit = async () => {
    if (kiiComponentRef.current) {
      kiiComponentRef.current.submit();
    }
  };

  const onClose = () => {
    hide(false);
  };

  const handleSuccessfulMatch = useCallback(async () => {
    fsEvent(`${kiiSource}_match_bucketed`);
    hide(true);
  }, [kiiSource, hide]);

  const handleFailedMatch = useCallback(async () => {
    hide(true);
  }, [hide]);

  const handleLoadingFailed = useCallback(() => {
    setViewType('title_typeahead');
  }, []);

  const handleSkip = useCallback(async () => {
    fsEvent(`${kiiSource}_skipped`);

    if (viewType === 'title_typeahead') {
      hide(true);
    } else {
      setViewType('title_typeahead');
    }
  }, [kiiSource, viewType, hide]);

  const kiiComponentRef = useRef<KiiComponentHandle>(null);

  return (
    <StyledModal onClose={onClose} open={open}>
      <ModalContents>
        <TopContainer>
          <TopImage src={$image_url(imageName)} alt={''} />
          <HeadingContainer>
            <HeadingText>{headingText}</HeadingText>
            <SubheadingText>{subheadingText}</SubheadingText>
          </HeadingContainer>
        </TopContainer>
        <InnerContainer>
          {viewType === 'paper_claiming' && (
            <PaperClaiming
              ref={kiiComponentRef}
              kiiSource={kiiSource}
              maxWorks={3}
              label="Select the paper you published"
              onSkip={handleSkip}
              onSuccessfulMatch={handleSuccessfulMatch}
              onFailedMatch={handleFailedMatch}
              onLoadingFailed={handleLoadingFailed}
            />
          )}
          {viewType === 'title_typeahead' && (
            <PaperTitleContainer>
              <PaperTitle
                ref={kiiComponentRef}
                kiiSource={kiiSource}
                label="Type the title of the latest paper you published"
                onSkip={handleSkip}
                onSuccessfulMatch={handleSuccessfulMatch}
                onFailedMatch={handleFailedMatch}
              />
            </PaperTitleContainer>
          )}
        </InnerContainer>
        <Button
          iconRight="arrow_forward"
          onClick={onSubmit}
          fullWidth={windowWidth <= 450}
        >
          Submit
        </Button>
      </ModalContents>
    </StyledModal>
  );
};
